import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
    AccordionDetails,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import type { AccordionProps } from '@mui/material/Accordion';
import MuiAccordion from '@mui/material/Accordion';
import type { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import MuiAccordionSummary, { accordionSummaryClasses } from '@mui/material/AccordionSummary';
import { styled } from '@mui/material/styles';
import { groupBy } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Chip from '/@/components/bayer/chip';
import ModalV2 from '/@/components/bayer/modal-v2';
import colors from '/@/components/theme-provider/colors';
import { setsUnion } from '/@/core/set';
import settings from '/@/core/settings';
import { useLocations } from '/@/hooks/session';
import { useSetUserLocations, useUserLocations } from '/@/hooks/user';
import { useZones } from '/@/hooks/zones';
import { _ } from '/@/locale';
import type { Country } from '/@/store/session/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import SearchInput from './components/search-input';

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
    ({ theme }) => ({
        p: 0,
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 0,
        },
        '&::before': {
            display: 'none',
        },
    })
);

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary expandIcon={<KeyboardArrowDownIcon fontSize='24px' />} {...props} />
))(() => ({
    [`&.${accordionSummaryClasses.root}`]: {
        padding: 0,
    },
    [`& .${accordionSummaryClasses.content}`]: {
        margin: 0,
        alignItems: 'center',
    },
}));

const LocationsSelectorModal = ({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) => {
    const [selectedLocations, setSelectedLocations] = useState<Map<number, Set<number>>>(new Map());
    const [selectedZones, setSelectedZones] = useState<Set<'template' | 'north'>>(new Set());
    const [expandChips, setExpandChips] = useState(false);

    const country = useSelector(getSessionCountry);
    const provincesQuery = useLocations({ level: 1, parentId: country!.id, pageSize: 100000 }, { enabled: !!country });
    const locationsQuery = useLocations(
        {
            level: 2,
            parentIdIn: (provincesQuery.data?.results || []).map(({ id }) => id),
            pageSize: 100000,
        },
        { enabled: !!provincesQuery.data }
    );
    const zonesQuery = useZones({ type: 'corn' }, { enabled: true });

    const provinces = useMemo<Country[]>(() => provincesQuery.data?.results || [], [provincesQuery.data?.results]);
    const locations = useMemo<Country[]>(() => locationsQuery.data?.results || [], [locationsQuery.data?.results]);
    const zones = useMemo(() => zonesQuery.data || [], [zonesQuery.data]);

    const groupedProvincesById = useMemo(() => new Map(provinces.map((el) => [el.id, el])), [provinces]);
    const groupedLocationsById = useMemo(() => new Map(locations.map((el) => [el.id, el])), [locations]);

    const zonesLocations = useMemo(() => {
        return zones
            .filter((z) => z.country === country?.code)
            .map((zone) => {
                const zoneLocations = locations.filter((location) => location.zones.includes(zone.id));
                return { ...zone, locations: zoneLocations };
            });
    }, [country?.code, locations, zones]);

    const groupedZonesWithLocations = useMemo<Record<'template' | 'north', Set<number>>>(() => {
        const zonesCodes = {
            template: ['Temprano', 'Tardío'],
            north: ['NEA', 'NOA'],
        };

        const output = Object.entries(zonesCodes).reduce(
            (acc, [zoneType, zoneNames]) => {
                const zones = zonesLocations.filter((z) => zoneNames.some((code) => z.codename.includes(code)));
                acc[zoneType] = setsUnion(
                    new Set(acc[zoneType]),
                    new Set(zones.flatMap((z) => z.locations.map((l) => l.id)))
                );
                return acc;
            },
            {} as Record<'template' | 'north', Set<number>>
        );

        return output;
    }, [zonesLocations]);

    const locationsByProvince = useMemo(() => {
        let newSelectedZones = new Set(selectedZones);
        if (newSelectedZones.size === 0) newSelectedZones = new Set(['template', 'north']);

        const filteredLocationsIds = Array.from(newSelectedZones)
            .map((zoneId) => groupedZonesWithLocations[zoneId])
            .reduce((allLocationIds, locationIds) => {
                return setsUnion(allLocationIds, locationIds);
            }, new Set());

        return groupBy(
            locations.filter(({ id }) => filteredLocationsIds.has(id)),
            'parentId'
        );
    }, [groupedZonesWithLocations, locations, selectedZones]);

    const provincesWithLocations = useMemo(() => {
        const provincesWithChildren = provinces
            .map((province) => ({
                ...province,
                children: locationsByProvince[province.id] || [],
            }))
            .filter((province) => province.children.length > 0);
        return new Map(provincesWithChildren.map((province) => [province.id, province]));
    }, [provinces, locationsByProvince]);

    const toggleLocation = useCallback((provinceId: number, locationId: number) => {
        setSelectedLocations((prev) => {
            const newMap = new Map(prev);
            // Copy existing set or create a new one so we don't mutate the previous state.
            const selectedSet = newMap.get(provinceId) ? new Set(newMap.get(provinceId)) : new Set<number>();
            if (selectedSet.has(locationId)) {
                selectedSet.delete(locationId);
            } else {
                selectedSet.add(locationId);
            }
            newMap.set(provinceId, selectedSet);
            return newMap;
        });
    }, []);

    const toggleProvince = useCallback(
        (provinceId: number, checked: boolean) => {
            setSelectedLocations((prev) => {
                const newMap = new Map(prev);
                const province = provincesWithLocations.get(provinceId);
                if (province) {
                    if (checked) {
                        // Select all locations for the province
                        const allLocations = new Set(province.children.map((location) => location.id));
                        newMap.set(provinceId, allLocations);
                    } else {
                        // Deselect all locations for the province
                        newMap.set(provinceId, new Set());
                    }
                }
                return newMap;
            });
        },
        [provincesWithLocations]
    );

    const provinceSelectionStatus = useMemo(
        () =>
            new Map(
                Array.from(provincesWithLocations).map(([provinceId, province]) => {
                    const children = province.children || [];
                    const selectedSet = selectedLocations.get(provinceId) || new Set<number>();
                    const some = children.some(({ id }) => selectedSet.has(id));
                    const every = children.length > 0 && children.every(({ id }) => selectedSet.has(id));
                    return [provinceId, { some, every }];
                })
            ),
        [provincesWithLocations, selectedLocations]
    );

    const AllChips = useMemo(
        () =>
            Array.from(selectedLocations)
                .flatMap(([provinceId, provinceLocations]) => {
                    const { every } = provinceSelectionStatus.get(provinceId) || {};

                    if (every) {
                        const name = groupedProvincesById.get(provinceId)?.name;
                        if (!name) return null;
                        return (
                            <Chip
                                label={`${name} (${provinceLocations.size})`}
                                onDelete={() => {
                                    toggleProvince(provinceId, false);
                                }}
                                sx={{
                                    background: colors.themes[settings.app.theme].mui.primary.infoBackground,
                                }}
                            />
                        );
                    }

                    return Array.from(provinceLocations).map((locationId) => {
                        const name = groupedLocationsById.get(locationId)?.name;
                        if (!name) return null;
                        return (
                            <Chip
                                key={locationId}
                                label={name}
                                onDelete={() => {
                                    toggleLocation(provinceId, locationId);
                                }}
                                sx={{
                                    background: colors.themes[settings.app.theme].mui.primary.infoBackground,
                                }}
                            />
                        );
                    });
                })
                .filter(Boolean),
        [
            groupedLocationsById,
            groupedProvincesById,
            provinceSelectionStatus,
            selectedLocations,
            toggleLocation,
            toggleProvince,
        ]
    );

    const userLocationsQuery = useUserLocations({ level: 2, pageSize: 100000 }, { enabled: !!country });

    const userLocations = useMemo<Country[]>(
        () => userLocationsQuery.data?.results || [],
        [userLocationsQuery.data?.results]
    );

    // When userLocations is loaded, group them by their province (parentId)
    // and mark all locations as selected.
    useEffect(() => {
        const newSelectedLocations = new Map<number, Set<number>>();
        userLocations.forEach((loc) => {
            // Assuming each loc has both an id and a parentId property
            const provinceId = loc.parentId;
            if (!newSelectedLocations.has(provinceId)) {
                newSelectedLocations.set(provinceId, new Set());
            }
            newSelectedLocations.get(provinceId)!.add(loc.id);
        });
        setSelectedLocations(newSelectedLocations);
    }, [userLocations]);

    // When either the userLocations or the grouped zones change,
    // auto-select a zone if any user-selected location is in that zone.
    useEffect(() => {
        const userLocationIds = new Set(userLocations.map((loc) => loc.id));
        const newSelectedZones = new Set<'template' | 'north'>();

        (['template', 'north'] as const).forEach((zoneKey) => {
            const zoneLocationIds = groupedZonesWithLocations[zoneKey] || new Set<number>();
            // If any location from the user is included in the zone, mark the zone as selected.
            for (const id of zoneLocationIds) {
                if (userLocationIds.has(id)) {
                    newSelectedZones.add(zoneKey);
                    break;
                }
            }
        });

        setSelectedZones((oldSelectedZones) => setsUnion(oldSelectedZones, newSelectedZones));
    }, [userLocations, groupedZonesWithLocations]);

    const setUserLocations = useSetUserLocations();

    return (
        <ModalV2
            open={open}
            onClose={() => {
                setOpen(false);
            }}
            title={_('Indicanos la ubicación agroecológica de los productores')}
            actions={
                <Button
                    variant='contained'
                    color='secondary'
                    onClick={async () => {
                        try {
                            await setUserLocations.trigger(
                                Array.from(selectedLocations.values()).flatMap((set) => Array.from(set))
                            );
                            setOpen(false);
                            enqueueSnackbar(_('Las ubicaciones fueron guardadas con éxito.'));
                        } catch (error) {
                            enqueueSnackbar(_('Ocurrio un error al intentar guardar. Intenta nuevamente.'), {
                                variant: 'error',
                            });
                        }
                    }}
                    disabled={
                        setUserLocations.isMutating ||
                        Array.from(selectedLocations).every(([_, locations]) => locations.size === 0)
                    }
                    startIcon={setUserLocations.isMutating ? <CircularProgress size={20} /> : undefined}
                >
                    {_('Guardar')}
                </Button>
            }
            actionsProps={{
                sx: {
                    justifyContent: 'flex-end',
                    mt: 'auto',
                },
            }}
            maxWidth='md'
            sx={{
                '& .MuiContainer-root': {
                    p: { xs: 0, sm: 3 },
                    height: { xs: '100%', sm: 'inherit' },
                    '& > .MuiPaper-root': {
                        height: { xs: '100%', sm: 'inherit' },
                        overflow: {
                            xs: 'scroll',
                            sm: 'inherit',
                        },
                    },
                },
            }}
        >
            <Stack
                spacing={2}
                sx={{
                    height: { xs: '100vh', sm: 'calc(100vh - 200px)' },
                    textAlign: 'left',
                    overflow: { xs: 'auto', sm: 'hidden' },
                    width: '100%',
                }}
            >
                <Typography variant='body1' color='primary.mui.inactive'>
                    {_(
                        'Conocer la ubicación nos ayudará a ofrecerte recomendaciones más precisas para los productores.'
                    )}
                </Typography>
                <Stack direction='row' spacing={2}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectedZones.has('template')}
                                size='large'
                                onChange={(_, checked) => {
                                    setSelectedZones((selectedZones) => {
                                        const newSelectedZones = new Set(selectedZones);
                                        if (checked) {
                                            newSelectedZones.add('template');
                                        } else {
                                            newSelectedZones.delete('template');
                                        }
                                        return newSelectedZones;
                                    });
                                }}
                                color='secondary'
                            />
                        }
                        label={_('Zona Templada')}
                        sx={{
                            '& .MuiTypography-root': {
                                color: (t) => t.palette.primary.main,
                            },
                        }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectedZones.has('north')}
                                size='large'
                                onChange={(_, checked) => {
                                    setSelectedZones((selectedZones) => {
                                        const newSelectedZones = new Set(selectedZones);
                                        if (checked) {
                                            newSelectedZones.add('north');
                                        } else {
                                            newSelectedZones.delete('north');
                                        }
                                        return newSelectedZones;
                                    });
                                }}
                                color='secondary'
                            />
                        }
                        label={_('Zona Norte')}
                        sx={{
                            '& .MuiTypography-root': {
                                color: (t) => t.palette.primary.main,
                            },
                        }}
                    />
                </Stack>
                <SearchInput
                    provincesWithLocations={provincesWithLocations}
                    toggleLocation={toggleLocation}
                    selectedLocations={selectedLocations}
                />
                <Stack spacing={0.5}>
                    <Stack direction='row' justifyContent='space-between' alignItems='center'>
                        <Typography variant='caption' color='primary.mui.inactive'>
                            {_('{{addedLocations}} ubicaciones agregadas', {
                                addedLocations: Array.from(selectedLocations).reduce(
                                    (acc, [_, locations]) => acc + locations.size,
                                    0
                                ),
                            })}
                        </Typography>
                        <Link
                            sx={[
                                {
                                    ml: 'auto',
                                    width: 'auto',
                                },
                                AllChips.length === 0 && {
                                    pointerEvents: 'none',
                                },
                            ]}
                        >
                            <Typography
                                onClick={() => {
                                    setSelectedLocations(new Map());
                                }}
                                sx={[
                                    {
                                        cursor: 'pointer',
                                    },
                                    AllChips.length === 0 && {
                                        color: (t) => t.palette.primary.mui.inactive,
                                    },
                                ]}
                                color='primary'
                                variant='caption'
                            >
                                {_('Borrar todo')}
                            </Typography>
                        </Link>
                    </Stack>
                    <Stack
                        direction='row'
                        flexWrap='wrap'
                        spacing={0.5}
                        useFlexGap
                        sx={{
                            maxHeight: '100px',
                            overflow: 'auto',
                        }}
                    >
                        {AllChips.slice(0, expandChips ? AllChips.length : 5)}
                    </Stack>

                    {AllChips.length > 5 && (
                        <Link
                            sx={{ width: 'auto', textAlign: 'left', cursor: 'pointer' }}
                            onClick={() => setExpandChips((prev) => !prev)}
                        >
                            <Stack direction='row' alignItems='center'>
                                <Typography color='primary' variant='caption'>
                                    {expandChips ? _('Ver menos') : _('Ver mas')}
                                </Typography>

                                <KeyboardArrowDownIcon
                                    fontSize='18px'
                                    sx={[
                                        expandChips && {
                                            transform: 'rotate(180deg)',
                                        },
                                    ]}
                                />
                            </Stack>
                        </Link>
                    )}
                </Stack>
                {locationsQuery.isValidating ? (
                    <Stack sx={{ height: '100%' }} alignItems='center'>
                        <CircularProgress size={20} />
                    </Stack>
                ) : (
                    <Stack sx={{ overflow: 'auto', height: '100%' }}>
                        {Array.from(provincesWithLocations).map(([provinceId, province]) => {
                            const { some, every } = provinceSelectionStatus.get(provinceId) || {};
                            return (
                                <Accordion key={provinceId}>
                                    <AccordionSummary>
                                        <Checkbox
                                            value={provinceId}
                                            checked={every}
                                            indeterminate={some && !every}
                                            size='large'
                                            sx={{ p: 1.5 }}
                                            onChange={(event, checked) => {
                                                event.stopPropagation();
                                                toggleProvince(provinceId, checked);
                                            }}
                                            color='secondary'
                                        />
                                        <Typography
                                            component='span'
                                            variant='subtitle1'
                                            fontWeight='bold'
                                            color='primary'
                                        >
                                            {province.name} ({province.children.length})
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ py: 0 }}>
                                        <Stack ml={2}>
                                            {province.children.map((location, index) => {
                                                const isActive =
                                                    selectedLocations.get(provinceId)?.has(location.id) || false;
                                                return (
                                                    <FormControlLabel
                                                        key={location.id}
                                                        control={
                                                            <Checkbox
                                                                value={location.id}
                                                                checked={isActive}
                                                                size='large'
                                                                onChange={() => toggleLocation(provinceId, location.id)}
                                                                color='info'
                                                            />
                                                        }
                                                        label={location.name}
                                                        sx={[
                                                            index !== province.children.length - 1 && {
                                                                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                                            },
                                                            isActive && {
                                                                backgroundColor: '#F5F5F5',
                                                            },
                                                            {
                                                                '& .MuiTypography-root': {
                                                                    color: (t) => t.palette.primary.main,
                                                                },
                                                            },
                                                        ]}
                                                    />
                                                );
                                            })}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </Stack>
                )}
            </Stack>
        </ModalV2>
    );
};

export default LocationsSelectorModal;
